import { Permission } from '@act/shared/authorization';
import {
  NeedDto,
  NeedsActionDto,
  NeedsActivityDto,
} from '@act/shared/data-transfer-objects';
import type { NeedAddActionRequest } from '@act/shared/types';
import { zodResolver } from '@hookform/resolvers/zod';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import {
  persistCboForResourceReferrals,
  recordCboUsageForResourceReferrals,
} from 'react-components/forms/cboResourceForm/persistCboForResourceReferrals.util';
import type { AddNeedResourceDetailsToActionRequest } from '@act/shared/types';
import { usePlacesAutocompleteEnabled } from 'react-components/places/usePlacesAutocompleteEnabled.hook';
import { needRequests } from 'redux/requests/need.requests';
import { actingUserAppSelectors } from 'redux/slices/actingUserSlice.slice';
import { metaSliceSelectors } from 'redux/slices/metaSlice.hook';
import { getMemberNeeds } from 'redux/slices/needs.slice';
import { useAppDispatch } from 'redux/storeHooks';
import { useApiCall } from 'redux/utils/apiHook.hook';
import { useToast } from 'react-components/app/ReusableToast';
import { useAddActionFormConfig } from '../AddActionDialogFormConfig';
import {
  ActionFormResourceDetails,
  ActionFormValues,
  NeedActionFormSchema,
} from '../components/useNeedActionFormtypes';
import useConfirmationAlertDialog from './useConfirmationAlertDialog.hook';
import useRichMessageDialog from './useRichMessageDialog.hook';
import useUnsavedChangesDialog from './useUnsavedChangesDialog.hook';
import {
  buildActionFormResetValues,
  getDefaultActionResourceDetails,
} from './addActionDialogForm.util';
import {
  formatNeedActionResourceReferrals,
  ResourceDetailsRequestWithId,
} from './formatNeedActionResourceReferrals.util';
import { selectActivityOptionsForNeedAction } from './needActionActivityOptions.util';

export interface UseAddActionDialogFormParams {
  need: NeedDto;
  action?: NeedsActionDto;
  setOpen: (open: boolean) => void;
}

/**
 * Form state, handlers, and FormBuilder config for {@link AddActionDialog}.
 *
 * @param params - Need context and dialog open setter.
 * @returns Dialog form API for rendering and submission.
 */
export function useAddActionDialogForm({
  need,
  action,
  setOpen,
}: UseAddActionDialogFormParams) {
  const dispatch = useAppDispatch();
  const canDeleteNeedActions = actingUserAppSelectors.useUserHasPermission(
    Permission.CAN_DELETE_NEED_ACTIONS
  );
  const [showError, setShowError] = useState(false);
  const toast = useToast();
  const handleUnsavedChanges = useUnsavedChangesDialog();
  const openLearnMoreDialog = useRichMessageDialog();
  const openAbandonResourceDialog = useConfirmationAlertDialog();
  const openConfirmDeleteActionDialog = useConfirmationAlertDialog();
  const [deletedAction, isDeletingAction, deleteAction] = useApiCall(
    needRequests.deleteAction
  );
  const researchActivity =
    metaSliceSelectors.useSelectResourceResearchActivity();
  const isResourcingResearch =
    action?.activityId && action.activityId === researchActivity?.id;
  const overrideValues = useMemo<Partial<ActionFormValues>>(
    () =>
      isResourcingResearch && researchActivity?.id
        ? {
            activityId: researchActivity.id,
            markAsFulfilled: false,
          }
        : {},
    [isResourcingResearch, researchActivity?.id]
  );

  const referralsCountRef = useRef<number | null>(null);
  const pendingCboUsageRef = useRef<{
    needId: number;
    referrals: AddNeedResourceDetailsToActionRequest[];
  } | null>(null);
  const [selectedActivity, setSelectedActivity] =
    useState<NeedsActivityDto | null>(null);
  const [expandedForm, setExpandedForm] = useState<string | null>(null);
  const [durationFocused, setDurationFocused] = useState(false);
  const minDate = DateTime.now().minus({ week: 1 });
  const { activities, issues } =
    metaSliceSelectors.useSelectNeedsMetadata().data;
  const issue = useMemo(
    () => issues.find(i => i.id === need?.issueId),
    [need?.issueId, issues]
  );
  const activityOptions = useMemo(
    () =>
      issue
        ? selectActivityOptionsForNeedAction(activities, issue, action)
        : [],
    [action, activities, issue]
  );

  const placesAutocompleteEnabled = usePlacesAutocompleteEnabled();
  const { control, handleSubmit, reset, setValue, watch, formState, getValues } =
    useForm<ActionFormValues>({
      defaultValues: {
        activityId: overrideValues.activityId,
        date: undefined,
        duration: '',
        description: undefined,
        resourceReferred: false,
        resourceDetails: [],
        markAsFulfilled: overrideValues.markAsFulfilled,
      },
      criteriaMode: 'all',
      mode: 'onBlur',
      resolver: zodResolver(NeedActionFormSchema),
    });

  const {
    append,
    remove,
    fields: resourceDetails,
  } = useFieldArray({
    name: 'resourceDetails',
    control,
    keyName: 'key',
  });

  const formValues = watch();
  const referrals = watch('resourceDetails');

  useEffect(() => {
    if (!action) return;
    const resetValues = buildActionFormResetValues({
      action,
      overrideValues,
      placesAutocompleteEnabled,
    });

    referralsCountRef.current = resetValues.resourceDetails?.length ?? 0;

    reset(resetValues);
  }, [action, overrideValues, placesAutocompleteEnabled, reset]);

  useEffect(() => {
    if (!formValues?.activityId) {
      setSelectedActivity(null);
      return;
    }
    setSelectedActivity(
      activities.find(a => a.id === formValues.activityId) ?? null
    );
  }, [formValues?.activityId, activities]);

  useEffect(() => {
    if ((referralsCountRef.current ?? 0) > resourceDetails.length) {
      referralsCountRef.current = (referralsCountRef.current ?? 0) - 1;
    }
  }, [resourceDetails.length]);

  useEffect(() => {
    if (resourceDetails.length >= (referralsCountRef.current ?? 0)) {
      referralsCountRef.current = resourceDetails.length;
      setExpandedForm(resourceDetails[resourceDetails.length - 1]?.key ?? null);
    }
  }, [resourceDetails]);

  const getTimeWindowMessage = () => {
    if (!selectedActivity) return 'Select an activity';
    return `The target time window for this action is ~${selectedActivity.defaultTimeFrameInMinutes} minutes.`;
  };

  const expandResourceForm = (key: string) => {
    setExpandedForm(expandedForm === key ? null : key);
  };

  const appendResourceDetails = () => {
    append(getDefaultActionResourceDetails());
  };

  const confirmDeleteResource = async (index: number) => {
    const confirm = await openAbandonResourceDialog({
      title: 'Confirm delete',
      icon: <ErrorOutlineIcon />,
      iconColor: 'error',
      iconBorderColor: 'errorContainer',
      avatarCaption: 'Are you sure?',
      message: 'Are you sure you want to delete this resource?',
      confirmationText: 'Yes, delete now',
      confirmButtonColor: 'error',
    });
    if (confirm) {
      remove(index);
      if (formValues.resourceReferred && referrals.length === 1) {
        setValue('resourceReferred', false);
      }
    }
  };

  const [createdAction, isProcessing, addAction] = useApiCall(
    needRequests.addActionToNeed
  );

  const confirmDeleteAction = async () => {
    const confirm = await openConfirmDeleteActionDialog({
      title: 'Confirm delete',
      icon: <ErrorOutlineIcon />,
      iconColor: 'error',
      iconBorderColor: 'errorContainer',
      avatarCaption: 'Are you sure?',
      message: 'Are you sure you want to delete this action?',
    });
    if (confirm) {
      deleteAction({ params: { needId: need?.id, actionId: action?.id } });
    }
  };

  useEffect(() => {
    if (!createdAction || isProcessing) {
      return;
    }

    const pending = pendingCboUsageRef.current;
    if (pending) {
      void recordCboUsageForResourceReferrals(
        pending.needId,
        createdAction.id,
        pending.referrals
      ).finally(() => {
        pendingCboUsageRef.current = null;
      });
    }

    toast('Action created successfully');
    reset();
    setShowError(false);
    setOpen(false);
  }, [createdAction, isProcessing, toast, reset, setOpen]);

  useEffect(() => {
    if (deletedAction && !isDeletingAction) {
      toast('Action deleted successfully');
      if (need?.memberId) {
        dispatch(getMemberNeeds({ params: { memberId: need.memberId } }));
      }
      reset();
      setShowError(false);
      setOpen(false);
    }
  }, [
    deletedAction,
    isDeletingAction,
    toast,
    reset,
    setOpen,
    dispatch,
    need?.memberId,
  ]);

  const handleSubmission = handleSubmit(async (formData: ActionFormValues) => {
    setShowError(false);
    try {
      const finalData = { ...formData, ...overrideValues };
      const resources =
        finalData.resourceReferred && finalData.resourceDetails?.length > 0
          ? placesAutocompleteEnabled && need?.id
            ? await persistCboForResourceReferrals(
                finalData.resourceDetails as ActionFormResourceDetails[],
                need.id
              )
            : formatNeedActionResourceReferrals(
                finalData.resourceDetails as ResourceDetailsRequestWithId[]
              )
          : null;

      pendingCboUsageRef.current =
        placesAutocompleteEnabled &&
        resources?.some(r => r.cboRecordId) &&
        need?.id
          ? { needId: need.id, referrals: resources }
          : null;

      const body: NeedAddActionRequest = {
        activityId: finalData.activityId,
        date: finalData.date,
        durationInMinutes: Number(finalData.duration),
        description: finalData.description,
        ...(action && {
          originalActionId: action.originalActionId ?? action.id,
        }),
        markAsFulfilled: finalData.markAsFulfilled,
        ...(resources && { resourceReferrals: resources }),
      };
      addAction({ body, params: { needId: need?.id } });
    } catch {
      pendingCboUsageRef.current = null;
      setShowError(true);
    }
  });

  const { isDirty } = formState;

  const handleClose = useCallback(
    () => {
      if (isProcessing) return;
      if (isDirty) {
        void handleUnsavedChanges(() => {
          reset();
          setShowError(false);
          setOpen(false);
        });
      } else {
        reset();
        setShowError(false);
        setOpen(false);
      }
    },
    [handleUnsavedChanges, isDirty, isProcessing, reset, setOpen]
  );

  const formConfig = useAddActionFormConfig({
    overrideValues,
    issue,
    activityOptions,
    selectedActivity,
    setValue,
    getTimeWindowMessage,
    openLearnMoreDialog,
    minDate,
    control,
    watch,
    durationFocused,
    setDurationFocused,
    formValues,
    resourceDetails,
    formState,
    expandedForm,
    expandResourceForm,
    confirmDeleteResource,
    appendResourceDetails,
    referralsCountRef,
    need,
    placesAutocompleteEnabled,
    getValues,
    append,
    remove,
  });

  return {
    showError,
    formConfig,
    control,
    formState,
    isProcessing,
    handleClose,
    handleSubmission,
    confirmDeleteAction,
    canDeleteNeedActions,
    isResourcingResearch,
  };
}
